import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useFeatures } from '@/lib/useFeatures';
import { notifyXp } from '@/lib/xpToast';
import { Camera, Upload, Trash2, Share2, Eye, EyeOff, Lock, Flame, RefreshCw, Sparkles, Calendar } from 'lucide-react';

/**
 * PROOF OF LIFE
 *
 * Private daily photo journal. Each photo becomes a "soul token" — a small
 * tile coloured from the photo's dominant palette with mood + vibe notes.
 *
 * On day 90 the full mosaic unlocks — a 9×10 grid view of the full journey.
 */

const MOOD_RING = {
  calm: 'from-blue-400 to-indigo-700',
  hope: 'from-amber-300 to-yellow-700',
  grit: 'from-orange-500 to-red-800',
  focus: 'from-zinc-400 to-zinc-900',
  joy: 'from-pink-300 to-rose-700',
  tired: 'from-slate-500 to-slate-900',
  searching: 'from-zinc-500 to-amber-700',
  peace: 'from-emerald-300 to-emerald-800',
  fire: 'from-amber-400 to-red-800',
  love: 'from-rose-400 to-fuchsia-800',
};

function tileGradient(palette = ['#D4A437']) {
  const cols = (palette || []).filter(Boolean).slice(0, 3);
  if (cols.length === 0) return 'linear-gradient(135deg, #D4A437, #1a1a1a)';
  if (cols.length === 1) return `linear-gradient(135deg, ${cols[0]}, #0b0b0b)`;
  return `linear-gradient(135deg, ${cols.join(', ')})`;
}

function fullUrl(path) {
  if (!path) return '';
  if (/^https?:/i.test(path)) return path;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${path}`;
}

export default function ProofOfLife() {
  const { user } = useAuth();
  const { isOn, features, loading: flagsLoading } = useFeatures();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [caption, setCaption] = useState('');
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/proof/me');
      setData(data);
    } catch (e) {
      // Silent
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (flagsLoading) return <div className="kc-container py-16 text-muted-kc" data-testid="proof-loading">Waking the lens…</div>;
  if (!isOn('proof_of_life')) {
    return (
      <div className="kc-container py-16" data-testid="proof-disabled">
        <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Proof of Life</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Lens off.</h1>
        <p className="mt-3 text-muted-kc max-w-prose">Proof of Life is not active right now. Check back soon.</p>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="kc-container py-20 text-center" data-testid="proof-locked">
        <Lock size={32} className="mx-auto text-gold"/>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-3">Sign in to begin your mosaic.</h1>
        <p className="mt-3 text-muted-kc">Photos are private. The mosaic is yours alone — unless you choose to share it.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/login?next=/proof" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="proof-login">Log in</Link>
          <Link to="/signup" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-kc-strong font-semibold hover:bg-surface-2 transition-colors" data-testid="proof-signup">Create account</Link>
        </div>
      </div>
    );
  }

  const onPick = () => fileRef.current?.click();

  const onFile = async (file) => {
    if (!file) return;
    if (data?.has_today) {
      const ok = window.confirm('You already logged today. Replace your existing token?');
      if (!ok) return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (caption) fd.append('caption', caption);
      const { data: row } = await api.post('/proof/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCaption('');
      if (row?.xp?.xp_gained) {
        notifyXp(row.xp, 'Proof of Life logged');
        window.dispatchEvent(new Event('kc:xp-updated'));
      }
      await load();
      setSelected(row);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const deleteDay = async (dateKey) => {
    if (!window.confirm('Delete this day from your journey?')) return;
    try {
      await api.delete(`/proof/${dateKey}`);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const toggleShare = async (entry) => {
    try {
      const { data: res } = await api.put(`/proof/${entry.date_key}/share`, { is_shared: !entry.is_shared });
      setData((d) => d && ({ ...d, entries: d.entries.map((e) => e.date_key === entry.date_key ? { ...e, is_shared: res.is_shared } : e) }));
      if (selected?.date_key === entry.date_key) setSelected({ ...entry, is_shared: res.is_shared });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Share toggle failed.');
    }
  };

  const entries = data?.entries || [];
  const daysLogged = data?.days_logged || 0;
  const dayInJourney = data?.day_in_journey || 0;
  const maxDays = data?.max_days || 90;
  const unlocked = data?.mosaic_unlocked;
  const hasToday = data?.has_today;
  const allowShare = !!features?.proof_of_life?.allow_share;

  // Build a fixed-size grid for the mosaic visualization.
  const slots = new Array(maxDays).fill(null);
  entries.forEach((e, i) => { if (i < maxDays) slots[i] = e; });

  return (
    <div className="bg-ink text-white min-h-[calc(100vh-5rem)]" data-testid="proof-page">
      {/* Hero / today */}
      <div className="kc-container py-12 md:py-16">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-gold">Proof of Life</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] mt-3">One photo.<br/>One day.<br/>Ninety chances.</h1>
            <p className="mt-5 text-white/65 text-base md:text-lg max-w-prose">
              Every day for 90 days, take ONE photo. Anything. The light through your window. Your coffee.
              A street. We don't show you the mosaic until day 90 — but each day becomes a small token,
              coloured by what you saw.
            </p>

            <div className="mt-7 grid grid-cols-3 gap-3 max-w-md" data-testid="proof-stats">
              <Stat label="Day" value={dayInJourney || '—'}/>
              <Stat label="Logged" value={daysLogged}/>
              <Stat label="Until reveal" value={Math.max(0, maxDays - daysLogged)}/>
            </div>

            {/* Capture CTA */}
            <div className="mt-8" data-testid="proof-capture">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="proof-file-input"/>
              {!hasToday ? (
                <button
                  type="button"
                  onClick={onPick}
                  disabled={uploading}
                  className="h-14 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors disabled:opacity-40"
                  data-testid="proof-cta-take"
                >
                  {uploading ? <><RefreshCw size={16} className="animate-spin"/> Capturing…</> : <><Camera size={16}/> Capture today's photo</>}
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 text-sm text-white/65 bg-white/5 border border-white/10 rounded-[var(--kc-radius-sm)] px-4 py-3" data-testid="proof-today-done">
                  <Sparkles size={14} className="text-[color:var(--kc-gold)]"/>
                  You showed up today. Streak counted.
                  <button type="button" onClick={onPick} className="ml-3 text-xs uppercase tracking-wider text-white/55 hover:text-white" data-testid="proof-replace">Replace</button>
                </div>
              )}
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Optional caption — for your eyes only."
                rows={2}
                maxLength={280}
                className="mt-3 w-full bg-white/5 border border-white/10 focus:border-[color:var(--kc-gold)] outline-none text-sm rounded-[var(--kc-radius-sm)] p-3 transition-colors resize-none"
                data-testid="proof-caption"
              />
            </div>

            {error && <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-200 rounded-[var(--kc-radius-sm)] p-3 text-sm" data-testid="proof-error">{error}</div>}
          </div>

          {/* Lock card / reveal */}
          <div className="bg-white/[0.04] border border-white/10 rounded-[var(--kc-radius-lg)] p-6 md:p-8 relative overflow-hidden" data-testid="proof-vault">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,rgba(212,164,55,0.35),transparent_55%)]" aria-hidden/>
            <div className="relative">
              <div className="text-[11px] tracking-[0.18em] uppercase text-gold">The Mosaic</div>
              <div className="font-display uppercase text-2xl md:text-3xl mt-1">{unlocked ? 'Unlocked.' : `${maxDays - daysLogged} days to reveal.`}</div>
              <div className="text-sm text-white/60 mt-1">{unlocked ? "Your 90-day soul portrait is below." : "Each day is a tile. The full image only resolves at day 90."}</div>

              {/* Locked mosaic preview */}
              <div className="mt-5 grid grid-cols-9 gap-1.5 aspect-[9/10] max-h-[420px]" data-testid="proof-mosaic">
                {slots.map((entry, i) => {
                  const isFuture = i >= daysLogged;
                  if (!entry) {
                    return <div key={i} className={`rounded-sm ${isFuture ? 'bg-white/[0.04]' : 'bg-white/10 border border-white/15'}`} aria-hidden/>;
                  }
                  return (
                    <button
                      key={entry.date_key}
                      type="button"
                      onClick={() => setSelected(entry)}
                      className={`rounded-sm transition-transform hover:scale-110 hover:z-10 relative ${selected?.date_key === entry.date_key ? 'ring-2 ring-[color:var(--kc-gold)]' : ''}`}
                      style={{ background: tileGradient(entry.palette) }}
                      title={`${entry.date_key} · ${entry.vibe || ''}`}
                      data-testid={`proof-tile-${entry.date_key}`}
                    />
                  );
                })}
              </div>

              {!unlocked && (
                <div className="mt-4 text-center" data-testid="proof-lock">
                  <Lock size={14} className="inline-block text-white/45 mr-1"/>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-white/45">Reveal locked · {daysLogged} / {maxDays}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selected day card */}
      {selected && (
        <div className="border-t border-white/10 bg-black" data-testid="proof-detail">
          <div className="kc-container py-10 md:py-12">
            <div className="grid md:grid-cols-[1fr_1fr] gap-6 max-w-5xl">
              <div>
                <div className="aspect-square rounded-[var(--kc-radius-md)] overflow-hidden border border-white/10 bg-black">
                  <img src={fullUrl(selected.photo_url)} alt="" className="w-full h-full object-cover"/>
                </div>
                {selected.caption && <p className="mt-3 text-sm text-white/65 italic">{selected.caption}</p>}
              </div>
              <div className="flex flex-col gap-3 justify-center">
                <div className="text-[11px] tracking-[0.18em] uppercase text-gold inline-flex items-center gap-2">
                  <Calendar size={12}/> {selected.date_key}
                </div>
                {selected.mood && (
                  <div className="inline-flex items-center gap-2 text-sm">
                    <span className={`inline-block w-3 h-3 rounded-full bg-gradient-to-br ${MOOD_RING[selected.mood] || MOOD_RING.searching}`} aria-hidden/>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-white/60">Mood</span>
                    <span className="font-semibold uppercase tracking-wide">{selected.mood}</span>
                  </div>
                )}
                {selected.energy && (
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/55 inline-flex items-center gap-2"><Flame size={12} className="text-orange-400"/> Energy {selected.energy} / 5</div>
                )}
                {selected.subject && <div className="text-white/60 text-sm">Today's frame: <span className="text-white">{selected.subject}</span></div>}
                {selected.vibe && (
                  <p className="font-display uppercase text-xl md:text-2xl leading-tight text-[color:var(--kc-gold)] mt-2 forge-gold-shimmer" data-testid="proof-vibe">
                    "{selected.vibe}"
                  </p>
                )}
                {selected.palette && selected.palette.length > 0 && (
                  <div className="mt-3 flex gap-1.5">
                    {selected.palette.map((c, i) => (
                      <span key={i} className="w-7 h-7 rounded-full border border-white/15" style={{ background: c }} title={c} aria-label={`Palette color ${c}`}/>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  {allowShare && (
                    <button type="button" onClick={() => toggleShare(selected)} className={`h-10 px-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border text-sm font-semibold transition-colors ${selected.is_shared ? 'bg-[color:var(--kc-gold)] text-black border-[color:var(--kc-gold)]' : 'border-white/20 text-white/80 hover:bg-white/10'}`} data-testid="proof-share-toggle">
                      {selected.is_shared ? <><Eye size={14}/> Public</> : <><EyeOff size={14}/> Private</>}
                    </button>
                  )}
                  <button type="button" onClick={() => deleteDay(selected.date_key)} className="h-10 px-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/15 text-sm font-semibold text-white/70 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/30 transition-colors" data-testid="proof-delete">
                    <Trash2 size={14}/> Delete day
                  </button>
                  <button type="button" onClick={() => setSelected(null)} className="h-10 px-3 inline-flex items-center text-sm text-white/55 hover:text-white" data-testid="proof-close">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-[var(--kc-radius-md)] p-3">
    <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">{label}</div>
    <div className="font-display text-2xl md:text-3xl text-[color:var(--kc-gold)] leading-tight mt-1">{value}</div>
  </div>
);
